import { Controller } from "stimulus"

export default class extends Controller {
  static values = { 
    minimumQueryLength: Number,
    url: String,
    state: String,
    selected: String,
    addNewEntryItem: Boolean
  }

  get options() {
    return Array.from(this.resultsList.querySelectorAll('li'))
  }

  get selected() {
    return this.resultsList.querySelector('[aria-selected="true"]')
  }

  stateValueChanged() {
    switch(this.stateValue) {
      case 'querying':
        if (this.container.contains(this.resultsList)) this.container.removeChild(this.resultsList)
        this.container.appendChild(this.queryingView)
        break;
      case 'completed':
        if (this.container.contains(this.queryingView)) this.container.removeChild(this.queryingView)
        this.container.appendChild(this.resultsList)
        break;
      case 'committed':
      case 'waiting':
        if (this.container.contains(this.resultsList)) this.container.removeChild(this.resultsList)
        if (this.container.contains(this.queryingView)) this.container.removeChild(this.queryingView)
      default:
        break;
    } 
  }

  setupQueryInput() {
    this.queryInput = document.createElement('input')
    this.queryInput.type = 'text'
    this.queryInput.classList = 'sa-field-input'
    this.queryInput.autocomplete = 'off'
    this.queryInput.spellcheck = false
    this.queryInput.disabled = this.element.dataset.attributesDisabled
    this.queryInput.required = this.element.dataset.attributesRequired
    this.queryInput.placeholder = this.element.dataset.attributesPlaceholder
    this.queryInput.id = this.element.dataset.attributesId
  }

  initialize() {
    if (!this.minimumQueryLengthValue) {
      this.minimumQueryLengthValue = 3
    }

    this.setupQueryInput();

    this.hiddenInput = document.createElement('input')
    this.hiddenInput.type = 'hidden'
    this.hiddenInput.name = this.element.dataset.attributesName
    this.hiddenInput.value = this.selectedValue

    this.container = document.createElement('div')
    this.container.classList = 'sa-autocomplete-query-container'

    this.queryingView = document.createElement('div')
    this.queryingView.classList = 'sa-autocomplete-loading-container'

    let btn = document.createElement('button')
    btn.classList = 'sa-autocomplete-loading-spinner'
    btn.innerHTML = 'Loading'
    this.queryingView.appendChild(btn)

    this.resultsList = document.createElement('ul')
    this.resultsList.classList = 'sa-autocomplete-list'

    this.element.appendChild(this.queryInput)
    this.element.appendChild(this.hiddenInput)
    this.element.appendChild(this.container)

    this.query = this.query.bind(this)
    this.focusout = this.focusout.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
    this.onResultsClick = this.onResultsClick.bind(this)
  }

  connect() {
    this.queryInput.addEventListener('input', this.query)
    this.queryInput.addEventListener('focusout', this.focusout)
    this.queryInput.addEventListener('keydown', this.onKeydown)
    this.resultsList.addEventListener('click', this.onResultsClick)
    this.resultsList.addEventListener('mousedown', this.onResultsClick)

    if (this.selectedValue) {
      this.onConnectQuery()
    }
  }

  disconnect() {
    this.queryInput.removeEventListener('input', this.query)
    this.queryInput.removeEventListener('focusout', this.focusout)
    this.queryInput.removeEventListener('keydown', this.onKeydown)
    this.resultsList.removeEventListener('click', this.onResultsClick)
    this.resultsList.removeEventListener('mousedown', this.onResultsClick)
  }

  focusout() {
    if (this.stateValue != 'committed') {
      event.stopPropagation()
      event.preventDefault()
      this.clearResults()
      this.clearSelected()
      this.clearQueryInput()
      this.stateValue = 'waiting'
    }
  }

  onKeydown(event) {
    switch (event.key) {
      case "Escape":
        if (this.stateValue != 'waiting') {
          event.stopPropagation()
          event.preventDefault()
          this.clearResults()
          this.clearSelected()
          this.clearQueryInput()
          this.stateValue = 'waiting'
        }
        break;
      case "ArrowDown":
        {
          event.preventDefault()

          const newItem = this.selectNextItem(this.selected, true)
          if (!newItem) {
            if (this.options.length > 0) {
              // if we array all the way through, select the last item
              this.click(this.options.slice(-1).pop())
            } else {
              this.queryInput.focus()
              this.queryInput.scrollIntoView(false)
            }
          }
        }

        break;
      case "ArrowUp":
        {
          event.preventDefault()

          const newItem = this.selectNextItem(this.selected, false)
          if (!newItem) {
            if (this.options[0]) {
              this.click(this.options[0])
            } else {
              this.queryInput.focus()
              this.queryInput.scrollIntoView(false)
            }
          }
        }
        break;
      case "Tab":
        {
          if (this.stateValue != 'committed') {
            const newItem = this.selectNextItem(this.selected, true)
            if (newItem) {
              event.stopPropagation()
              event.preventDefault()
            } else {
              if (this.options.length > 0) {
                // if we tab all the way through, select the last item
                this.click(this.options.slice(-1).pop())
              } else {
                this.clearResults()
                this.clearSelected()
                this.clearQueryInput()
                this.stateValue = 'waiting'
              }
            }
          }
        }

        break;
      case "Enter":
        event.stopPropagation()
        event.preventDefault()

        if (this.selected) {
          this.click(this.selected)
        }

        break;
    }
  }

  selectNextItem(current, forward = true) {
    const currentIndex = this.options.indexOf(current)
    const index = currentIndex + (forward ? 1 : -1)

    if (index >= 0 && this.options[index]) {
      const target = this.options[index]
      this.select(target)
      target.scrollIntoView(false)
      return target
    }

    return null
  }

  select(target) {
    for (const el of this.resultsList.querySelectorAll('[aria-selected="true"]')) {
      el.removeAttribute("aria-selected")
      el.classList.remove("active")
    }

    target.setAttribute("aria-selected", "true")
    target.classList.add("active")
  }

  commit() {
    const _selected = this.selected
    if (!_selected) return;

    this.hiddenInput.value = _selected.dataset.value
    this.queryInput.value = _selected.dataset.text
    this.stateValue = 'committed'
  }

  onResultsClick(event) {
    this.click(event.target)
  }

  click(target) {
    // Custom/New Entry is li -> div -> span | span... so we need to go up the tree to get the li element
    if (target.dataset.value == -1) {
      target = target.closest('li')
    }

    this.select(target)
    this.commit()
    this.element.dispatchEvent(
      new CustomEvent('autocomplete-change', {
        bubbles: true,
        detail: {
          text: this.selected.dataset.text,
          value: this.selected.dataset.value
        }
      })
    )
  }

  clearQueryInput() {
    this.queryInput.value = '';
  }

  clearSelected() {
    for (const el of this.resultsList.querySelectorAll('[aria-selected="true"]')) {
      el.removeAttribute("aria-selected")
      el.classList.remove("active")
    }

    this.hiddenInput.value = null
    this.selectedValue = null
  }

  clearResults() {
    while (this.resultsList.firstChild) {
      this.resultsList.removeChild(this.resultsList.firstChild)
    }
  }

  async query(event) {
    this.clearSelected()
    this.stateValue = 'querying'

    if (this.queryInput?.value?.length >= this.minimumQueryLengthValue) {
      const response = await fetch(this.urlValue + '/autocomplete', { method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query: this.queryInput.value }) 
      })

      const query = await response.json()
      this.clearResults();
      query.results.forEach(result => {
        const li = document.createElement('li')
        li.dataset.value = result.value
        li.dataset.text = result.text
        li.innerHTML = result.text
        this.resultsList.appendChild(li)
      })

      if (this.addNewEntryItemValue) {
        this.addNewEntryItem()
      }

      this.stateValue = 'completed'
    }
  }

  async onConnectQuery() {
    const response = await fetch(this.urlValue + '/autocomplete', { method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ids: [this.selectedValue] }) 
    })

    const query = await response.json()
    const result = query.results[0]

    this.hiddenInput.value = result.value
    this.queryInput.value = result.text
    this.stateValue = 'committed'
  }

  addNewEntryItem() {
    const li = document.createElement('li')
    let div = document.createElement('div')
    div.classList = 'flex flex-col'

    let header = document.createElement('span')
    header.classList = 'text-sm uppercase'
    header.innerHTML = 'New Entry'

    let text = document.createElement('span')
    text.classList = 'pl-6'
    text.dataset.value = -1
    text.dataset.custom = true
    text.innerHTML = this.queryInput.value
    
    div.appendChild(header)
    div.appendChild(text)
    li.appendChild(div)

    li.dataset.value = -1
    li.dataset.text = this.queryInput.value
    this.resultsList.appendChild(li)
  }

}